// #region Imports
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import WorkIcon from "@mui/icons-material/Work";
import {
  type MRT_Row,
} from "material-react-table";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import { FeeScheduleTemplateLoadIssue, isFeeScheduleTemplateLoadIssue } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import Guid from "_shared/guids/guid";
// #endregion

// #region Props
type FeeScheduleToolbarProps = {
  row: MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>;
  activeTab: PageTab;
  onArchivedChange: (id: Guid, archived: boolean) => void;
  onCreateProposal?: (template: FeeScheduleTemplate) => void;
  onDelete: (id: Guid) => void;
};

export default function FeeScheduleToolbar(props: Readonly<FeeScheduleToolbarProps>) {
  const { row, activeTab, onArchivedChange, onCreateProposal, onDelete } = props;

  if (isFeeScheduleTemplateLoadIssue(row.original)) return null;

  const template = row.original;
  const templateName = template.name || "fee schedule template";
  const isArchivedTab = activeTab === PageTab.Archived;
  const actions: RowAction[] = [];

  if (activeTab === PageTab.Templates) {
    actions.push(
      {
        key: "create-proposal",
        label: "Create Proposal",
        ariaLabel: `Create proposal from ${templateName}`,
        icon: <WorkIcon />,
        onClick: () => onCreateProposal?.(template),
      },
      {
        key: "delete",
        label: "Delete Template",
        icon: <DeleteIcon />,
        destructive: true,
        onClick: () => {
          if (!template.id) {
            console.warn("Attempting to delete a fee schedule template with no id");
            return;
          }
          onDelete(template.id);
        },
      },
    );
  }

  if (activeTab === PageTab.Templates || isArchivedTab) {
    actions.push({
      key: "archive",
      label: isArchivedTab ? "Unarchive" : "Archive",
      icon: isArchivedTab ? <UnarchiveIcon /> : <ArchiveIcon />,
      onClick: () => {
        if (!template.id) {
          console.warn("Attempting to archive/unarchive a fee schedule template with no id");
          return;
        }
        onArchivedChange(template.id, !isArchivedTab);
      },
    });
  }

  // #region Render
  return <RowActions actions={actions} ariaLabel={`Actions for ${templateName}`} />;
}
